import React from "react";
import { AlertTriangle, CheckCircle } from "lucide-react";

import { Badge } from "components/ui/badge";

/**
 * Render an entry's action specific parameters, e.g. the overlay used for a bug
 * insert or the channels a RIST restart targeted.
 */
function EntryDetails({ details }) {
    const entries = Object.entries(details || {});
    if (entries.length === 0) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {entries.map(([key, value]) => (
                <code key={key} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                    {key}: {Array.isArray(value) ? value.join(", ") : String(value)}
                </code>
            ))}
        </div>
    );
}

/**
 * ActionLogList Component
 *
 * The full recorded history of one action type for the current event, newest
 * first, so an operator can see who ran it and what happened.
 */
const ActionLogList = ({ entries, error }) => {
    if (error) {
        return (
            <small className="text-muted-foreground">
                Unable to load the action history.
            </small>
        );
    }

    if (!entries || entries.length === 0) {
        return <small className="text-muted-foreground">No actions recorded yet.</small>;
    }

    return (
        <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-border p-2">
            {entries.map((entry, index) => (
                <div
                    key={index}
                    className="space-y-1 border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={entry.status === "success" ? "success" : "destructive"}>
                            {entry.status === "success" ? (
                                <CheckCircle className="h-3.5 w-3.5" />
                            ) : (
                                <AlertTriangle className="h-3.5 w-3.5" />
                            )}
                            {entry.status === "success" ? "Success" : "Failed"}
                        </Badge>
                        <strong className="text-sm">{entry.user}</strong>
                        <small className="text-muted-foreground">{entry.timestamp}</small>
                        {entry.environment && (
                            <Badge variant="outline">{entry.environment}</Badge>
                        )}
                    </div>
                    {entry.message && (
                        <small className="block text-muted-foreground">{entry.message}</small>
                    )}
                    <EntryDetails details={entry.details} />
                </div>
            ))}
        </div>
    );
};

export default ActionLogList;
